import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Building2, Plus, Loader2, CheckCircle2, XCircle, Clock, AlertCircle,
  Globe, DollarSign, Languages, Store, Users, Package, RefreshCw
} from "lucide-react";

export function RestaurantManagementTab({ adminAxios }) {
  const [activeView, setActiveView] = useState("monitoring");
  const [loading, setLoading] = useState(true);
  const [monitoring, setMonitoring] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [creating, setCreating] = useState(false);

  const [formData, setFormData] = useState({
    restaurant_name: "",
    admin_email: "",
    admin_password: "",
    admin_name: "",
    phone: "",
    subdomain: "",
    country: "PT",
    currency: "EUR",
    language: "pt",
    template: "restaurante"
  });

  const loadData = async () => {
    setLoading(true);
    try {
      const [monitoringRes, templatesRes, currenciesRes, languagesRes] = await Promise.all([
        adminAxios.get("/admin/restaurants/monitoring"),
        adminAxios.get("/admin/restaurants/templates"),
        adminAxios.get("/admin/restaurants/currencies"),
        adminAxios.get("/admin/restaurants/languages")
      ]);
      setMonitoring(monitoringRes.data);
      setTemplates(templatesRes.data.templates || []);
      setCurrencies(currenciesRes.data.currencies || []);
      setLanguages(languagesRes.data.languages || []);
    } catch (err) {
      toast.error("Erro ao carregar dados");
    }
    setLoading(false);
  };

  useEffect(() => {
    loadData();
  }, [adminAxios]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!formData.restaurant_name || !formData.admin_email || !formData.admin_password) {
      toast.error("Preencha os campos obrigatórios");
      return;
    }
    if (formData.admin_password.length < 6) {
      toast.error("Senha deve ter no mínimo 6 caracteres");
      return;
    }
    setCreating(true);
    try {
      const res = await adminAxios.post("/admin/restaurants/create", formData);
      toast.success(res.data.message || "Restaurante criado com sucesso!");
      setFormData({
        restaurant_name: "",
        admin_email: "",
        admin_password: "",
        admin_name: "",
        phone: "",
        subdomain: "",
        country: "PT",
        currency: "EUR",
        language: "pt",
        template: "restaurante"
      });
      loadData();
      setActiveView("monitoring");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao criar restaurante");
    }
    setCreating(false);
  };

  const StatusIcon = ({ status }) => {
    switch (status) {
      case "active": return <CheckCircle2 className="w-4 h-4 text-emerald-500" />;
      case "ready": return <CheckCircle2 className="w-4 h-4 text-blue-500" />;
      case "in_progress": return <Clock className="w-4 h-4 text-amber-500" />;
      default: return <XCircle className="w-4 h-4 text-gray-400" />;
    }
  };

  const statusLabels = {
    active: "Ativo",
    ready: "Pronto para Operar",
    in_progress: "Em Onboarding",
    not_started: "Não Iniciado"
  };

  const statusColors = {
    active: "bg-emerald-100 text-emerald-700",
    ready: "bg-blue-100 text-blue-700",
    in_progress: "bg-amber-100 text-amber-700",
    not_started: "bg-gray-100 text-gray-500"
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="restaurant-management-tab">
      {/* Header Buttons */}
      <div className="flex items-center gap-3">
        <Button
          variant={activeView === "monitoring" ? "default" : "outline"}
          onClick={() => setActiveView("monitoring")}
          className="gap-2"
          data-testid="view-monitoring-btn"
        >
          <Store className="w-4 h-4" /> Monitoramento
        </Button>
        <Button
          variant={activeView === "create" ? "default" : "outline"}
          onClick={() => setActiveView("create")}
          className="gap-2"
          data-testid="view-create-btn"
        >
          <Plus className="w-4 h-4" /> Criar Restaurante
        </Button>
        <Button
          variant="outline"
          onClick={loadData}
          className="gap-2 ml-auto"
          data-testid="refresh-btn"
        >
          <RefreshCw className="w-4 h-4" /> Atualizar
        </Button>
      </div>

      {/* MONITORING VIEW */}
      {activeView === "monitoring" && monitoring && (
        <div className="space-y-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="bg-emerald-50 border-emerald-200">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-emerald-500 flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-black text-emerald-700">{monitoring.counts.active}</p>
                  <p className="text-xs text-emerald-600">Ativos</p>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-black text-blue-700">{monitoring.counts.ready_to_operate}</p>
                  <p className="text-xs text-blue-600">Prontos para Operar</p>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-amber-50 border-amber-200">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-amber-500 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-black text-amber-700">{monitoring.counts.in_onboarding}</p>
                  <p className="text-xs text-amber-600">Em Onboarding</p>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-gray-50 border-gray-200">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gray-400 flex items-center justify-center">
                  <AlertCircle className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-black text-gray-600">{monitoring.counts.not_started}</p>
                  <p className="text-xs text-gray-500">Não Iniciados</p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Total */}
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Building2 className="w-4 h-4" />
            <span>Total de restaurantes: <strong>{monitoring.total_restaurants}</strong></span>
          </div>

          {/* Restaurants Lists */}
          <div className="grid md:grid-cols-2 gap-6">
            {["active", "ready_to_operate", "in_onboarding", "not_started"].map((status) => {
              const list = monitoring.restaurants[status] || [];
              if (list.length === 0) return null;
              return (
                <Card key={status}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <StatusIcon status={status === "ready_to_operate" ? "ready" : status === "in_onboarding" ? "in_progress" : status} />
                      {statusLabels[status === "ready_to_operate" ? "ready" : status === "in_onboarding" ? "in_progress" : status]}
                      <Badge variant="outline" className="ml-auto">{list.length}</Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2 max-h-64 overflow-y-auto">
                    {list.map((r) => (
                      <div key={r.id} className="flex items-center gap-3 p-2 rounded-lg bg-muted/50 hover:bg-muted transition-colors">
                        <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center text-emerald-700 font-bold text-xs">
                          {r.name?.charAt(0)?.toUpperCase() || "R"}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{r.name}</p>
                          <p className="text-xs text-muted-foreground truncate">{r.slug}</p>
                        </div>
                        <div className="text-right text-xs">
                          <div className="flex items-center gap-1 text-muted-foreground">
                            <Package className="w-3 h-3" /> {r.products_count || 0}
                          </div>
                          <div className="flex items-center gap-1 text-muted-foreground">
                            <Users className="w-3 h-3" /> {r.categories_count || 0} cat
                          </div>
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* CREATE VIEW */}
      {activeView === "create" && (
        <div className="max-w-2xl mx-auto">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Plus className="w-5 h-5 text-emerald-600" />
                Criar Novo Restaurante
              </CardTitle>
              <CardDescription>
                Preencha os dados para criar um novo restaurante com menu template inicial
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleCreate} className="space-y-6">
                {/* Dados do Restaurante */}
                <div className="space-y-4">
                  <h3 className="text-sm font-bold text-muted-foreground uppercase tracking-wide flex items-center gap-2">
                    <Building2 className="w-4 h-4" /> Dados do Restaurante
                  </h3>
                  <div className="grid gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="restaurant_name">Nome do Restaurante *</Label>
                      <Input
                        id="restaurant_name"
                        value={formData.restaurant_name}
                        onChange={(e) => setFormData(prev => ({ ...prev, restaurant_name: e.target.value }))}
                        placeholder="Ex: Pizzaria da Vila"
                        data-testid="input-restaurant-name"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="subdomain">Subdomínio (opcional)</Label>
                      <div className="flex items-center gap-2">
                        <Input
                          id="subdomain"
                          value={formData.subdomain}
                          onChange={(e) => setFormData(prev => ({ ...prev, subdomain: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') }))}
                          placeholder="pizzaria-da-vila"
                          data-testid="input-subdomain"
                        />
                        <span className="text-xs text-muted-foreground">.kaisosystem.com</span>
                      </div>
                      <p className="text-xs text-muted-foreground">Deixe vazio para gerar automaticamente</p>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone">Telefone</Label>
                      <Input
                        id="phone"
                        value={formData.phone}
                        onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                        placeholder="+351 912 345 678"
                        data-testid="input-phone"
                      />
                    </div>
                  </div>
                </div>

                {/* Administrador */}
                <div className="space-y-4">
                  <h3 className="text-sm font-bold text-muted-foreground uppercase tracking-wide flex items-center gap-2">
                    <Users className="w-4 h-4" /> Administrador
                  </h3>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="admin_email">Email *</Label>
                      <Input
                        id="admin_email"
                        type="email"
                        value={formData.admin_email}
                        onChange={(e) => setFormData(prev => ({ ...prev, admin_email: e.target.value }))}
                        placeholder="admin@restaurante.com"
                        data-testid="input-admin-email"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="admin_password">Senha *</Label>
                      <Input
                        id="admin_password"
                        type="password"
                        value={formData.admin_password}
                        onChange={(e) => setFormData(prev => ({ ...prev, admin_password: e.target.value }))}
                        placeholder="Mínimo 6 caracteres"
                        data-testid="input-admin-password"
                        required
                        minLength={6}
                      />
                    </div>
                    <div className="space-y-2 sm:col-span-2">
                      <Label htmlFor="admin_name">Nome do Administrador</Label>
                      <Input
                        id="admin_name"
                        value={formData.admin_name}
                        onChange={(e) => setFormData(prev => ({ ...prev, admin_name: e.target.value }))}
                        placeholder="Nome completo"
                        data-testid="input-admin-name"
                      />
                    </div>
                  </div>
                </div>

                {/* Configurações */}
                <div className="space-y-4">
                  <h3 className="text-sm font-bold text-muted-foreground uppercase tracking-wide flex items-center gap-2">
                    <Globe className="w-4 h-4" /> Configurações
                  </h3>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label>País</Label>
                      <Select
                        value={formData.country}
                        onValueChange={(v) => setFormData(prev => ({ ...prev, country: v }))}
                      >
                        <SelectTrigger data-testid="select-country">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="PT">Portugal</SelectItem>
                          <SelectItem value="BR">Brasil</SelectItem>
                          <SelectItem value="ES">Espanha</SelectItem>
                          <SelectItem value="US">Estados Unidos</SelectItem>
                          <SelectItem value="GB">Reino Unido</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Moeda</Label>
                      <Select
                        value={formData.currency}
                        onValueChange={(v) => setFormData(prev => ({ ...prev, currency: v }))}
                      >
                        <SelectTrigger data-testid="select-currency">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {currencies.map((c) => (
                            <SelectItem key={c.code} value={c.code}>
                              {c.symbol} {c.name} ({c.code})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Idioma</Label>
                      <Select
                        value={formData.language}
                        onValueChange={(v) => setFormData(prev => ({ ...prev, language: v }))}
                      >
                        <SelectTrigger data-testid="select-language">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {languages.map((l) => (
                            <SelectItem key={l.code} value={l.code}>
                              {l.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Template de Menu</Label>
                      <Select
                        value={formData.template}
                        onValueChange={(v) => setFormData(prev => ({ ...prev, template: v }))}
                      >
                        <SelectTrigger data-testid="select-template">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {templates.map((t) => (
                            <SelectItem key={t.id} value={t.id}>
                              {t.name} ({t.categories_count} categorias, {t.products_count} produtos)
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                {/* Template Preview */}
                {formData.template && (
                  <div className="p-4 rounded-lg bg-muted/50 border">
                    <p className="text-xs font-medium text-muted-foreground mb-2">Categorias do template selecionado:</p>
                    <div className="flex flex-wrap gap-2">
                      {templates.find(t => t.id === formData.template)?.categories.map((cat, i) => (
                        <Badge key={i} variant="outline" className="text-xs">{cat}</Badge>
                      ))}
                    </div>
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={creating}
                  className="w-full h-12 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-bold"
                  data-testid="create-restaurant-btn"
                >
                  {creating ? (
                    <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Criando...</>
                  ) : (
                    <><Plus className="w-4 h-4 mr-2" /> Criar Restaurante</>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
